import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePushRegistration } from './hooks/usePushRegistration';
import { IncomingCallOverlay } from './components/IncomingCallOverlay';
import { InstallPrompt } from './components/InstallPrompt';
import { BottomNav } from './components/BottomNav';
import InstallGuidePage from './pages/InstallGuidePage';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { ProtectedRoute } from './components/ProtectedRoute';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { PermissionsPage } from './pages/PermissionsPage';
import { LegalPage } from './pages/LegalPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { DoctorDashboard } from './pages/DoctorDashboard';
import { PharmacistDashboard } from './pages/PharmacistDashboard';
import { PatientDashboard } from './pages/PatientDashboard';
import { DoctorsListPage } from './pages/DoctorsListPage';
import { BookingPage } from './pages/BookingPage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
import { InstantCallPage } from './pages/InstantCallPage';
import { PsychologyJoinCallPage } from './pages/PsychologyJoinCallPage';
import { AIChatPage } from './pages/AIChatPage';
import { DoctorProfilePage } from './pages/DoctorProfilePage';
import { DoctorCallRoomPage } from './pages/DoctorCallRoomPage';
import { PhysioPackagesPage } from './pages/PhysioPackagesPage';
import { PhysioBookingPage } from './pages/PhysioBookingPage';
import { PhysioManagerDashboard } from './pages/PhysioManagerDashboard';
import { PsychologyDoctorsPage } from './pages/PsychologyDoctorsPage';
import { PaymentStatusPage } from './pages/PaymentStatusPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { PaymentFailedPage } from './pages/PaymentFailedPage';
import { JoinDoctorPharmacistPage, JobApplicationPage } from './pages/ApplicationFormPage';
import { PsychologyHomePage } from './pages/PsychologyHomePage';
import { PsychologyCategoryPage } from './pages/PsychologyCategoryPage';
import { Toaster } from './components/ui/sonner';
import { BackButton } from './components/BackButton';
import { useScrollReveal } from './hooks/useScrollReveal';
import './App.css';

const ConditionalNavbar = () => {
  const location = useLocation();
  // Hide global Navbar on home and patient dashboard (these have their own integrated headers)
  const noNavbar = ['/', '/patient'];
  if (noNavbar.includes(location.pathname)) return null;
  return <Navbar />;
};

// Wraps every routed page in a fade-in container and drives scroll-reveal.
const RouteShell = ({ children }) => {
  const location = useLocation();
  useScrollReveal(location.pathname);
  return (
    <div key={location.pathname} className="page-enter">
      {children}
    </div>
  );
};

// Splash page removed — visitors land on the homepage immediately.
// Everyone (first-time visitors, returning users, signed-in users) sees the
// full homepage with the services section. Permissions interstitial is now
// only available at the explicit /permissions route.
const HomeEntry = () => <HomePage />;

// Small inner component so we can call `useAuth` inside the AuthProvider tree
// and register/re-register the Web-Push subscription whenever the user changes.
const PushBridge = () => {
  const { user } = useAuth();
  usePushRegistration({ user });
  return null;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <div className="App">
            <PushBridge />
            <IncomingCallOverlay />
            <InstallPrompt />
            <BottomNav />
            <ConditionalNavbar />
            <BackButton />
            <RouteShell>
              <Routes>
              <Route path="/" element={<HomeEntry />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/permissions" element={<PermissionsPage />} />
              <Route path="/install" element={<InstallGuidePage />} />
              <Route path="/legal/:type" element={<LegalPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/doctors" element={<DoctorsListPage />} />
              <Route path="/subscriptions" element={<SubscriptionsPage />} />
              <Route path="/physio/packages" element={<PhysioPackagesPage />} />
              <Route path="/physio" element={<PhysioPackagesPage />} />
              <Route path="/apply/doctor" element={<JoinDoctorPharmacistPage />} />
              <Route path="/apply/jobs" element={<JobApplicationPage />} />
              <Route path="/psychology" element={<PsychologyHomePage />} />
              <Route path="/psychology/:categoryId" element={<PsychologyCategoryPage />} />
              <Route
                path="/psychology/doctors"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <PsychologyDoctorsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/physio-manager"
                element={
                  <ProtectedRoute allowedRoles={['physio_manager']}>
                    <PhysioManagerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments/success/:paymentId"
                element={
                  <ProtectedRoute>
                    <PaymentSuccessPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments/failed/:paymentId"
                element={
                  <ProtectedRoute>
                    <PaymentFailedPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments/:paymentId"
                element={
                  <ProtectedRoute>
                    <PaymentStatusPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/physio/book/:packageId"
                element={
                  <ProtectedRoute>
                    <PhysioBookingPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/book/:doctorId"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <BookingPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/call/:doctorId"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <InstantCallPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/psychology/join-call/:callId"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <PsychologyJoinCallPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/ai-chat"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <AIChatPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/patient"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <PatientDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/doctor"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/doctor/profile"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorProfilePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/doctor/call/:callId"
                element={
                  <ProtectedRoute allowedRoles={['doctor', 'pharmacist']}>
                    <DoctorCallRoomPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pharmacist"
                element={
                  <ProtectedRoute allowedRoles={['pharmacist']}>
                    <PharmacistDashboard />
                  </ProtectedRoute>
                }
              />
            </Routes>
            </RouteShell>
            <Toaster />
          </div>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
